import React, { useState, useMemo } from 'react'
import { displayLoad } from '../../lib/units'
import type { UnitPreference } from '../../lib/units'
import { useActionDispatch } from '../../hooks/useActionDispatch'
import { useRefEquipment } from '../../hooks/useRefEquipment'
import weaponsJson from '../../../generator/sim/data/hm3-weapons.json'

// Map weapon name → hm penalty (null = no hand mode, non-null = can benefit from 2H grip)
const weaponHmMap = new Map<string, number | null>(
  weaponsJson.weapons.map((w: { name: string; hm?: number | null }) => [w.name, w.hm ?? null])
)
// Fallback map keyed by base name (strips parentheticals like " (6')")
const weaponHmBaseMap = new Map<string, number | null>()
for (const w of weaponsJson.weapons as Array<{ name: string; hm?: number | null }>) {
  const base = w.name.replace(/\s*\(.*$/, '').trim()
  if (!weaponHmBaseMap.has(base)) weaponHmBaseMap.set(base, w.hm ?? null)
}

function lookupWeaponHm(refName: string | null): number | null | undefined {
  if (!refName) return undefined
  if (weaponHmMap.has(refName)) return weaponHmMap.get(refName)
  return weaponHmBaseMap.get(refName)
}
import { EquipmentShopDialog } from './EquipmentShopDialog'

interface EquipmentItem {
  id: string
  item_name: string
  quantity: number
  weight_lbs: number | null
  value_denarii: number | null
  location: string | null
  is_carried: boolean
  notes: string | null
  parent_item_id: string | null
}

interface WeaponItem {
  id: string
  weapon_name: string
  ref_weapon_name: string | null
  weight_lbs: number
  is_equipped: boolean
  held_in: 'right' | 'left' | null
  two_handed: boolean
  is_primary: boolean
  hand_mode_penalty: number
}

interface ArmourItem {
  id: string
  item_name: string
  weight_lbs: number
  is_worn: boolean
}

interface Props {
  character: {
    equipment: EquipmentItem[]
    weapons?: WeaponItem[]
    armour?: ArmourItem[]
    funds_denarii: number
  }
  pref?: UnitPreference
  characterId?: string
  campaignId?: string
  canEdit?: boolean
}

const LOCATIONS = ['worn', 'belt', 'in_hand', 'backpack', 'saddlebag', 'cached', 'vehicle', 'ground']

export function EquipmentLoadoutPanel({ character, pref, characterId, campaignId, canEdit }: Props) {
  const items = character.equipment ?? []
  const weapons = character.weapons ?? []
  const armour = character.armour ?? []

  // Merge weapons and armour into display — unequipped items show on ground
  const weaponEquipItems: EquipmentItem[] = weapons.map(w => {
    const handLabel = w.two_handed ? ' (2H)' : w.held_in === 'right' ? ' (R)' : w.held_in === 'left' ? ' (L)' : ''
    const location = w.held_in != null ? 'in_hand' : w.is_equipped ? 'belt' : 'ground'
    return {
      id: `w-${w.id}`,
      item_name: w.weapon_name + (w.is_primary ? ' *' : '') + handLabel,
      quantity: 1,
      weight_lbs: w.weight_lbs, value_denarii: null,
      location,
      is_carried: location !== 'ground',
      notes: (() => {
        if (w.held_in == null) return 'weapon'
        if (w.two_handed) return 'weapon:held:2h'
        const hm = lookupWeaponHm(w.ref_weapon_name)
        return (hm !== undefined && hm !== null) ? 'weapon:held:can2h' : 'weapon:held'
      })(),
      parent_item_id: null,
    }
  })
  const armourEquipItems: EquipmentItem[] = armour.map(a => ({
    id: `a-${a.id}`, item_name: a.item_name, quantity: 1,
    weight_lbs: a.weight_lbs, value_denarii: null,
    location: a.is_worn ? 'worn' : 'ground',
    is_carried: a.is_worn, notes: 'armour', parent_item_id: null,
  }))

  const allItems = [...items, ...weaponEquipItems, ...armourEquipItems]
  const hasAnything = allItems.length > 0 || canEdit

  if (!hasAnything) return null

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Equipment</h3>
      <FundsControl funds={character.funds_denarii} characterId={characterId} campaignId={campaignId} canEdit={canEdit} />
      <LiveInventory items={allItems} pref={pref ?? 'imperial'} characterId={characterId} campaignId={campaignId} canEdit={canEdit} />
    </div>
  )
}

// ─── Funds Control ──────────────────────────────────────────────────────────

function FundsControl({ funds, characterId, campaignId, canEdit }: { funds: number; characterId?: string; campaignId?: string; canEdit?: boolean }) {
  const { dispatch } = useActionDispatch(characterId ?? '', campaignId ?? '')
  const [showForm, setShowForm] = useState(false)
  const [amount, setAmount] = useState(0)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const adjust = async (gain: boolean) => {
    if (!amount) return
    setBusy(true)
    try {
      await dispatch('ADJUST_FUNDS', { delta: gain ? amount : -amount, reason: reason || undefined })
      setAmount(0); setReason(''); setShowForm(false)
    } catch { /* */ }
    setBusy(false)
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
      <span className="font-semibold">Funds:</span> <span>{funds}d</span>
      {canEdit && (
        <>
          <button onClick={() => setShowForm(!showForm)}
            className="text-xs px-2 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark">adjust</button>
          {showForm && (
            <span className="flex flex-wrap items-center gap-1">
              <input type="number" min={0} value={amount} onChange={e => setAmount(parseInt(e.target.value) || 0)}
                className="w-16 px-1 py-0.5 border border-parchment-dark rounded text-xs" placeholder="amount" />
              <input value={reason} onChange={e => setReason(e.target.value)}
                className="w-24 px-1 py-0.5 border border-parchment-dark rounded text-xs" placeholder="reason" />
              <button onClick={() => adjust(true)} disabled={busy || !amount}
                className="text-xs px-1.5 py-0.5 bg-success text-white rounded disabled:opacity-50">+</button>
              <button onClick={() => adjust(false)} disabled={busy || !amount}
                className="text-xs px-1.5 py-0.5 bg-warning text-white rounded disabled:opacity-50">-</button>
            </span>
          )}
        </>
      )}
    </div>
  )
}

// ─── Live Inventory ─────────────────────────────────────────────────────────

function LiveInventory({ items, pref, characterId, campaignId, canEdit }: {
  items: EquipmentItem[]
  pref: UnitPreference
  characterId?: string
  campaignId?: string
  canEdit?: boolean
}) {
  const { dispatch } = useActionDispatch(characterId ?? '', campaignId ?? '')
  const { data: catalog } = useRefEquipment()
  const [busy, setBusy] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [showCatalog, setShowCatalog] = useState(false)
  const [showShop, setShowShop] = useState(false)
  const [catalogFilter, setCatalogFilter] = useState('')
  const [newName, setNewName] = useState('')
  const [newQty, setNewQty] = useState(1)
  const [newWeight, setNewWeight] = useState(0)
  const [newValue, setNewValue] = useState(0)
  const [newLocation, setNewLocation] = useState('backpack')
  const [newParent, setNewParent] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  const childrenMap = useMemo(() => {
    const map = new Map<string, EquipmentItem[]>()
    for (const item of items) {
      if (item.parent_item_id) {
        const list = map.get(item.parent_item_id) ?? []
        list.push(item)
        map.set(item.parent_item_id, list)
      }
    }
    return map
  }, [items])

  const topLevel = useMemo(() => items.filter(i => !i.parent_item_id), [items])
  const containers = useMemo(() => items.filter(i => childrenMap.has(i.id)), [items, childrenMap])

  const worn = topLevel.filter(i => i.location === 'worn')
  const belt = topLevel.filter(i => i.location === 'belt')
  const inHand = topLevel.filter(i => i.location === 'in_hand')
  const backpack = topLevel.filter(i => i.location === 'backpack')
  const saddlebag = topLevel.filter(i => i.location === 'saddlebag')
  const stored = topLevel.filter(i => i.location === 'cached' || i.location === 'vehicle')
  const ground = topLevel.filter(i => i.location === 'ground')
  const carried = topLevel.filter(i => i.is_carried)

  const itemWeight = (item: EquipmentItem): number => {
    const own = (item.weight_lbs ?? 0) * item.quantity
    return own + (childrenMap.get(item.id) ?? []).reduce((sum, c) => sum + (c.weight_lbs ?? 0) * c.quantity, 0)
  }

  const totalCarriedWeight = carried.reduce((sum, i) => sum + itemWeight(i), 0)

  // Determine where an item goes when picked up from the ground
  const pickupLocation = (item: EquipmentItem): string => {
    const name = item.item_name.toLowerCase()
    if (/backpack|saddlebag|chest|barrel|sack/.test(name)) return 'worn'
    if (/pouch|purse|waterskin|wineskin|flask|knife|dagger/.test(name)) return 'belt'
    if (/quiver|bow case/.test(name)) return 'belt'
    if (childrenMap.has(item.id)) return 'worn' // any container
    return 'backpack'
  }

  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id)
    try { await fn() } catch { /* */ }
    setBusy(null)
  }

  const toggleExpand = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const prefillFromCatalog = (item: { item_name: string; weight_lbs: number | null; value_denarii: number | null }) => {
    setNewName(item.item_name)
    setNewWeight(item.weight_lbs ?? 0)
    setNewValue(item.value_denarii ?? 0)
    setNewQty(1)
    setShowCatalog(false)
    setShowAdd(true)
  }

  const addItem = async () => {
    if (!characterId || !campaignId || !newName.trim()) return
    setBusy('add')
    try {
      await dispatch('ACQUIRE_EQUIPMENT', {
        item_name: newName.trim(), quantity: newQty,
        weight_lbs: newWeight || undefined, value_denarii: newValue || undefined,
        location: newLocation, parent_item_id: newParent || undefined,
      })
      setNewName(''); setNewQty(1); setNewWeight(0); setNewValue(0); setNewParent('')
      setShowAdd(false)
    } catch { /* */ }
    setBusy(null)
  }

  const renderItem = (item: EquipmentItem, indent = false) => {
    const children = childrenMap.get(item.id) ?? []
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(item.id)
    const weight = itemWeight(item)

    return (
      <React.Fragment key={item.id}>
        <tr className={`border-b border-parchment/50 ${indent ? 'bg-parchment/20' : ''}`}>
          <td className={`py-1 ${indent ? 'pl-6' : ''}`}>
            {hasChildren && (
              <button onClick={() => toggleExpand(item.id)} className="text-xs mr-1 text-ink-light w-4 inline-block">
                {isExpanded ? '▾' : '▸'}
              </button>
            )}
            {indent && <span className="text-ink-light/40 mr-1">└</span>}
            {item.item_name}
            {item.quantity === 0 && <span className="text-ink-light ml-1">(empty)</span>}
          </td>
          <td className="text-right">
            {item.quantity > 1 ? item.quantity : item.quantity === 0 ? '0' : ''}
            {canEdit && item.quantity > 0 && (
              <button onClick={() => run(item.id, () => dispatch('USE_ITEM', { equipment_id: item.id }))}
                disabled={busy === item.id}
                className="text-[10px] ml-1 px-1 py-0 text-ink-light border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50"
                title="Use one">-1</button>
            )}
          </td>
          <td className="text-right">
            {hasChildren ? (
              <span>
                <span className="text-ink-light">({displayLoad((item.weight_lbs ?? 0) * item.quantity, pref)})</span>
                {' '}{displayLoad(weight, pref)}
              </span>
            ) : weight > 0 ? displayLoad(weight, pref) : '—'}
          </td>
          {canEdit && (
            <td className="text-right space-x-1 whitespace-nowrap">
              {item.location === 'ground' ? (
                <>
                  {item.id.startsWith('w-') ? (
                    <>
                      <button onClick={() => run(item.id, () => dispatch('EQUIP_WEAPON', { weapon_id: item.id.slice(2) }))}
                        disabled={busy === item.id}
                        className="text-[10px] px-1.5 py-0.5 text-success border border-success/30 rounded hover:bg-green-50 disabled:opacity-50">Pick Up</button>
                      <button onClick={() => run(item.id, () => dispatch('ABANDON_WEAPON', { weapon_id: item.id.slice(2) }))}
                        disabled={busy === item.id}
                        className="text-[10px] px-1.5 py-0.5 text-danger border border-danger/30 rounded hover:bg-red-50 disabled:opacity-50">Abandon</button>
                    </>
                  ) : item.id.startsWith('a-') ? (
                    <>
                      <button onClick={() => run(item.id, () => dispatch('EQUIP_ARMOUR', { armour_id: item.id.slice(2) }))}
                        disabled={busy === item.id}
                        className="text-[10px] px-1.5 py-0.5 text-success border border-success/30 rounded hover:bg-green-50 disabled:opacity-50">Pick Up</button>
                      <button onClick={() => run(item.id, () => dispatch('ABANDON_ARMOUR', { armour_id: item.id.slice(2) }))}
                        disabled={busy === item.id}
                        className="text-[10px] px-1.5 py-0.5 text-danger border border-danger/30 rounded hover:bg-red-50 disabled:opacity-50">Abandon</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => run(item.id, () => dispatch('MOVE_EQUIPMENT', { equipment_id: item.id, location: pickupLocation(item) }))}
                        disabled={busy === item.id}
                        className="text-[10px] px-1.5 py-0.5 text-success border border-success/30 rounded hover:bg-green-50 disabled:opacity-50">Pick Up</button>
                      <button onClick={() => run(item.id, () => dispatch('DROP_EQUIPMENT', { equipment_id: item.id }))}
                        disabled={busy === item.id}
                        className="text-[10px] px-1.5 py-0.5 text-danger border border-danger/30 rounded hover:bg-red-50 disabled:opacity-50">Abandon</button>
                    </>
                  )}
                </>
              ) : item.id.startsWith('w-') ? (
                item.notes === 'weapon:held:2h' || item.notes === 'weapon:held' || item.notes === 'weapon:held:can2h' ? (
                  <span className="flex items-center gap-1 justify-end">
                    <button onClick={() => run(item.id, () => dispatch('RELEASE_WEAPON_FROM_HAND', { weapon_id: item.id.slice(2) }))}
                      disabled={busy === item.id}
                      className="text-[10px] px-1.5 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50"
                      title="Sheathe — move to belt">sheathe</button>
                    {item.notes === 'weapon:held:can2h' && (
                      <button onClick={() => run(item.id, () => dispatch('GRIP_WEAPON_TWO_HANDED', { weapon_id: item.id.slice(2) }))}
                        disabled={busy === item.id}
                        className="text-[10px] px-1.5 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50"
                        title="Re-grip two-handed">2H</button>
                    )}
                    <button onClick={() => run(item.id, () => dispatch('DROP_WEAPON_FROM_HAND', { weapon_id: item.id.slice(2) }))}
                      disabled={busy === item.id}
                      className="text-[10px] px-1 py-0.5 text-danger border border-danger/30 rounded hover:bg-red-50 disabled:opacity-50"
                      title="Drop to ground">drop</button>
                  </span>
                ) : (
                  <span className="flex items-center gap-1 justify-end">
                    <button onClick={() => run(item.id, () => dispatch('DRAW_WEAPON_TO_HAND', { weapon_id: item.id.slice(2), hand: 'right' }))}
                      disabled={busy === item.id}
                      className="text-[10px] px-1.5 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50"
                      title="Draw to right hand">→R</button>
                    <button onClick={() => run(item.id, () => dispatch('DRAW_WEAPON_TO_HAND', { weapon_id: item.id.slice(2), hand: 'left' }))}
                      disabled={busy === item.id}
                      className="text-[10px] px-1.5 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50"
                      title="Draw to left hand">→L</button>
                    <button onClick={() => run(item.id, () => dispatch('UNEQUIP_WEAPON', { weapon_id: item.id.slice(2) }))}
                      disabled={busy === item.id}
                      className="text-[10px] px-1 py-0.5 text-danger border border-danger/30 rounded hover:bg-red-50 disabled:opacity-50"
                      title="Drop to ground">drop</button>
                  </span>
                )
              ) : item.id.startsWith('a-') ? (
                <button onClick={() => run(item.id, () => dispatch('UNEQUIP_ARMOUR', { armour_id: item.id.slice(2) }))}
                  disabled={busy === item.id}
                  className="text-[10px] px-1 py-0.5 text-danger border border-danger/30 rounded hover:bg-red-50 disabled:opacity-50">drop</button>
              ) : (
                <>
                  {!item.parent_item_id && (
                    <select value={item.location ?? 'backpack'}
                      onChange={e => run(item.id, () => dispatch('MOVE_EQUIPMENT', { equipment_id: item.id, location: e.target.value }))}
                      disabled={busy === item.id}
                      className="text-[10px] px-0.5 py-0 border border-parchment-dark rounded">
                      {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  )}
                  <button onClick={() => run(item.id, () => dispatch('MOVE_EQUIPMENT', { equipment_id: item.id, location: 'ground' }))}
                    disabled={busy === item.id}
                    className="text-[10px] px-1 py-0.5 text-danger border border-danger/30 rounded hover:bg-red-50 disabled:opacity-50"
                    title={hasChildren ? `Drop + ${children.length} contents` : 'Drop'}>drop</button>
                </>
              )}
            </td>
          )}
        </tr>
        {isExpanded && children.map(child => renderItem(child, true))}
      </React.Fragment>
    )
  }

  // Returns tbody rows (section header + items) for inclusion in the unified table
  const renderSection = (title: string, list: EquipmentItem[]) => {
    if (list.length === 0) return null
    return (
      <>
        <tr>
          <td colSpan={canEdit ? 4 : 3} className="pt-3 pb-0.5">
            <span className="text-[10px] font-semibold text-ink-light uppercase tracking-wider">{title}</span>
          </td>
        </tr>
        {list.map(item => renderItem(item))}
      </>
    )
  }

  const filteredCatalog = catalog?.filter(c =>
    !catalogFilter || c.item_name.toLowerCase().includes(catalogFilter.toLowerCase()) || c.category.includes(catalogFilter.toLowerCase())
  ) ?? []

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-y-1 mb-2">
        <div className="text-xs text-ink-light">
          {items.length} items, {displayLoad(totalCarriedWeight, pref)} carried
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-1">
            <button onClick={() => setShowShop(true)}
              className="text-xs px-2 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark">
              Shop...
            </button>
            <button onClick={() => { setShowCatalog(!showCatalog); setShowAdd(false) }}
              className="text-xs px-2 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark">
              From Catalog
            </button>
            <button onClick={() => { setShowAdd(!showAdd); setShowCatalog(false) }}
              className="text-xs px-2 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark">
              + Custom
            </button>
          </div>
        )}
      </div>

      {showCatalog && (
        <div className="border border-parchment-dark rounded p-3 mb-3 bg-parchment max-h-64 overflow-y-auto">
          <input value={catalogFilter} onChange={e => setCatalogFilter(e.target.value)}
            placeholder="Search items..." autoFocus
            className="w-full px-2 py-1 border border-parchment-dark rounded text-sm mb-2" />
          <div className="space-y-0.5">
            {filteredCatalog.map(item => (
              <button key={item.item_key} onClick={() => prefillFromCatalog(item)}
                className="w-full text-left px-2 py-1 text-xs hover:bg-parchment-dark rounded flex justify-between">
                <span>
                  {item.item_name}
                  {item.is_container && <span className="text-blue-600 ml-1">[container]</span>}
                  {item.is_consumable && <span className="text-orange-600 ml-1">[consumable]</span>}
                </span>
                <span className="text-ink-light">
                  {item.weight_lbs ? `${item.weight_lbs} lb` : ''}
                  {item.value_denarii ? ` ${item.value_denarii}d` : ''}
                </span>
              </button>
            ))}
            {filteredCatalog.length === 0 && <p className="text-xs text-ink-light py-2">No matches.</p>}
          </div>
        </div>
      )}

      {showAdd && (
        <div className="border border-parchment-dark rounded p-3 mb-3 bg-parchment">
          <div className="grid grid-cols-2 gap-2 text-sm mb-2">
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-0.5">Item Name</label>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Qty</label>
              <input type="number" min={1} value={newQty} onChange={e => setNewQty(parseInt(e.target.value) || 1)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Location</label>
              <select value={newParent || newLocation} onChange={e => {
                const val = e.target.value
                if (LOCATIONS.includes(val)) { setNewLocation(val); setNewParent('') }
                else { setNewParent(val) }
              }} className="w-full px-2 py-1 border border-parchment-dark rounded text-sm">
                {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                {containers.map(c => <option key={c.id} value={c.id}>In: {c.item_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Weight (lbs)</label>
              <input type="number" min={0} step={0.1} value={newWeight} onChange={e => setNewWeight(parseFloat(e.target.value) || 0)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Value (d)</label>
              <input type="number" min={0} value={newValue} onChange={e => setNewValue(parseInt(e.target.value) || 0)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addItem} disabled={busy === 'add' || !newName.trim()}
              className="px-3 py-1 text-sm bg-success text-white rounded hover:opacity-90 disabled:opacity-50">Add</button>
            <button onClick={() => setShowAdd(false)}
              className="px-3 py-1 text-sm border border-parchment-dark rounded hover:bg-parchment-dark">Cancel</button>
          </div>
        </div>
      )}

      {items.length > 0 ? (
        <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[320px]">
          <thead>
            <tr className="border-b border-parchment-dark text-left">
              <th className="py-1 font-normal text-ink-light">Item</th>
              <th className="text-right font-normal text-ink-light w-10">Qty</th>
              <th className="text-right font-normal text-ink-light w-16">Weight</th>
              {canEdit && <th className="w-32"></th>}
            </tr>
          </thead>
          <tbody>
            {renderSection('In Hand', inHand)}
            {renderSection('Worn', worn)}
            {renderSection('Belt', belt)}
            {renderSection('Backpack', backpack)}
            {renderSection('Saddlebag', saddlebag)}
            {renderSection('Stored / Cached', stored)}
            {renderSection('On Ground', ground)}
          </tbody>
        </table>
        </div>
      ) : (
        <p className="text-ink-light text-sm">No items in inventory.{canEdit ? ' Use the buttons above to add equipment.' : ''}</p>
      )}

      {showShop && characterId && campaignId && (
        <EquipmentShopDialog
          characterId={characterId}
          campaignId={campaignId}
          containers={containers}
          onClose={() => setShowShop(false)}
        />
      )}
    </>
  )
}
